import { Box, IconButton, Spinner, Text, VStack } from "@chakra-ui/react-v3";
import { RiAddLine, RiCheckLine } from "react-icons/ri";
import { Document, Page } from "react-pdf";
import "react-pdf/dist/Page/TextLayer.css";
import "react-pdf/dist/Page/AnnotationLayer.css";

type PdfDocumentViewerProps = {
  pdfUrl: string | undefined;
  error: string | undefined;
  isLoading: boolean;
  pdfContainerRef: React.RefObject<HTMLDivElement>;
  isPanning: boolean;
  numPages: number | null;
  scale: number;
  onDocumentLoadSuccess: ({ numPages }: { numPages: number }) => void;
  onDocumentLoadError: (error: Error) => void;
  selectedPages: Set<number>;
  onTogglePageSelection: (pageNum: number) => void;
  disablePageSelection?: boolean;
};

export function PdfDocumentViewer({
  pdfUrl,
  error,
  isLoading,
  pdfContainerRef,
  isPanning,
  numPages,
  scale,
  onDocumentLoadSuccess,
  onDocumentLoadError,
  selectedPages,
  onTogglePageSelection,
  disablePageSelection = false,
}: PdfDocumentViewerProps) {
  return (
    <Box
      ref={pdfContainerRef}
      flex="1"
      overflowY="auto"
      overflowX="auto"
      display="flex"
      justifyContent="center"
      alignItems="flex-start"
      p={4}
      bg="bg.muted"
      minH="700px"
      cursor={isPanning ? "grab" : "default"}
      _active={isPanning ? { cursor: "grabbing" } : undefined}
    >
      {isLoading && (
        <VStack gap={4} py={12}>
          <Spinner size="xl" color="status.info" />
          <Text fontSize="sm" color="fg.muted">
            Loading PDF... This may take up to 30 seconds.
          </Text>
        </VStack>
      )}

      {error && !isLoading && (
        <VStack gap={2} py={12}>
          <Text fontSize="md" color="status.error" fontWeight="medium">
            Failed to load PDF
          </Text>
          <Text fontSize="sm" color="fg.muted">
            {error}
          </Text>
        </VStack>
      )}

      {pdfUrl && !error && (
        <Document
          file={pdfUrl}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={onDocumentLoadError}
          loading={
            <VStack gap={4} py={12}>
              <Spinner size="xl" color="status.info" />
              <Text fontSize="sm" color="fg.muted">
                Rendering PDF...
              </Text>
            </VStack>
          }
          error={
            <VStack gap={2} py={12}>
              <Text fontSize="md" color="status.error" fontWeight="medium">
                Failed to render PDF
              </Text>
              <Text fontSize="sm" color="fg.muted">
                The PDF file may be corrupted or incompatible.
              </Text>
            </VStack>
          }
        >
          <VStack gap={4} align="center">
            {numPages &&
              Array.from({ length: numPages }, (_, index) => {
                const pageNum = index + 1;
                const isSelected = selectedPages.has(pageNum);

                return (
                  <Box
                    key={`page_${pageNum}`}
                    position="relative"
                    boxShadow="0 2px 8px rgba(0, 0, 0, 0.15)"
                    bg="white"
                    css={{
                      /* Ensure page wrapper doesn't interfere with rendering */
                      contain: "layout style paint",
                      contentVisibility: "auto",
                    }}
                    outline={isSelected ? "3px solid" : "none"}
                    outlineColor={isSelected ? "btn.success.hoverBorder" : "transparent"}
                    _hover={{
                      "& > .page-select-overlay": {
                        opacity: 1,
                      },
                    }}
                  >
                    <Page
                      pageNumber={pageNum}
                      renderTextLayer={true}
                      renderAnnotationLayer={true}
                      scale={scale}
                      loading={
                        <Box py={12} px={20}>
                          <Spinner size="lg" color="status.info" />
                        </Box>
                      }
                    />
                    {!disablePageSelection && (
                      <Box
                        className="page-select-overlay"
                        position="absolute"
                        top={2}
                        right={2}
                        opacity={isSelected ? 1 : 0}
                        transition="opacity 0.2s"
                        zIndex={10}
                      >
                        <IconButton
                          aria-label={isSelected ? "Remove page from export" : "Add page to export"}
                          size="sm"
                          colorPalette={isSelected ? "green" : "blue"}
                          variant={isSelected ? "solid" : "outline"}
                          onClick={e => {
                            e.stopPropagation();
                            onTogglePageSelection(pageNum);
                          }}
                          bg={isSelected ? "green.500" : "white"}
                          _hover={{
                            bg: isSelected ? "green.600" : "gray.100",
                          }}
                        >
                          {isSelected ? <RiCheckLine /> : <RiAddLine />}
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                );
              })}
          </VStack>
        </Document>
      )}
    </Box>
  );
}
